/**
 * EvidenceTable Component
 *
 * Reusable table for displaying extracted evidence with citations and confidence scores.
 * Used by GroundRiskAnswers and AirRiskAnswers components to display SORA evidence.
 * Supports feedback button per row for user corrections.
 *
 * Requirements: 4.2, 4.3, 4.4
 */

import React from 'react';
import { ConfidenceBadge } from '../ConfidenceBadge';
import { CitationLink } from '../CitationLink';
import { FeedbackButton } from '../FeedbackButton';
import { Citation, ConfidenceLevel } from '../../types/extraction';
import { Feedback } from '../../types/feedback';
import styles from './EvidenceTable.module.css';

/**
 * Represents a column configuration for the evidence table
 */
export interface EvidenceColumn {
  /** Unique key for the column */
  key: string;
  /** Display label for the column header */
  label: string;
  /** Optional width for the column (CSS value) */
  width?: string;
}

/**
 * Represents an evidence item to display in the table
 */
export interface EvidenceItem {
  /** Unique identifier for the evidence item */
  id: string;
  /** Field name/label for the evidence */
  field: string;
  /** Extracted value from the document */
  extractedValue: string;
  /** Confidence level of the extraction */
  confidence: ConfidenceLevel;
  /** Citations supporting this extraction */
  citations: Citation[];
  /** User-corrected value if feedback was provided (optional) */
  userCorrectedValue?: string;
  /** Feedback record if provided (optional) */
  feedback?: Feedback;
}

/**
 * Props for the EvidenceTable component
 */
export interface EvidenceTableProps {
  /** Array of evidence items to display */
  items: EvidenceItem[];
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked for an item */
  onFeedbackClick: (itemId: string) => void;
  /** Column configuration for the table */
  columns: EvidenceColumn[];
  /** Optional loading state */
  isLoading?: boolean;
  /** Optional empty state message */
  emptyMessage?: string;
  /** Optional aria-label for the table */
  ariaLabel?: string;
}

/**
 * Default columns for the evidence table
 */
const DEFAULT_COLUMNS: EvidenceColumn[] = [
  { key: 'field', label: 'Field', width: '25%' },
  { key: 'value', label: 'Extracted Value', width: '30%' },
  { key: 'confidence', label: 'Confidence', width: '15%' },
  { key: 'citations', label: 'Citations', width: '20%' },
  { key: 'feedback', label: 'Feedback', width: '10%' },
];

/**
 * EvidenceTable displays extracted evidence in a table format with:
 * - Field name and extracted value
 * - Confidence badge with color coding
 * - Clickable citation links
 * - Feedback button for each row
 * - Visual distinction for user-corrected values
 *
 * @example
 * <EvidenceTable
 *   items={groundRiskAnswers}
 *   columns={columns}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const EvidenceTable: React.FC<EvidenceTableProps> = ({
  items,
  onCitationClick,
  onFeedbackClick,
  columns = DEFAULT_COLUMNS,
  isLoading = false,
  emptyMessage = 'No evidence items to display',
  ariaLabel = 'Evidence table',
}) => {
  // Render loading skeleton
  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <table className={styles.table} aria-label={ariaLabel} aria-busy="true">
          <thead className={styles.tableHeader}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={styles.headerCell}
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((index) => (
              <tr key={index} className={styles.skeletonRow}>
                <td className={styles.cell}>
                  <div className={`${styles.skeleton} ${styles.skeletonField}`} />
                </td>
                <td className={styles.cell}>
                  <div className={`${styles.skeleton} ${styles.skeletonValue}`} />
                </td>
                <td className={styles.cell}>
                  <div className={`${styles.skeleton} ${styles.skeletonBadge}`} />
                </td>
                <td className={styles.cell}>
                  <div className={`${styles.skeleton} ${styles.skeletonCitation}`} />
                </td>
                <td className={styles.cell}>
                  <div className={`${styles.skeleton} ${styles.skeletonButton}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render empty state
  if (items.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        <p className={styles.emptyMessage}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table} aria-label={ariaLabel}>
        <thead className={styles.tableHeader}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={styles.headerCell}
                style={{ width: column.width }}
                scope="col"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <EvidenceRow
              key={item.id}
              item={item}
              onCitationClick={onCitationClick}
              onFeedbackClick={onFeedbackClick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Props for the EvidenceRow component
 */
interface EvidenceRowProps {
  item: EvidenceItem;
  onCitationClick: (citation: Citation) => void;
  onFeedbackClick: (itemId: string) => void;
}

/**
 * EvidenceRow renders a single row in the evidence table
 */
const EvidenceRow: React.FC<EvidenceRowProps> = ({
  item,
  onCitationClick,
  onFeedbackClick,
}) => {
  const hasCorrectedValue = item.userCorrectedValue !== undefined;
  const hasFeedback = item.feedback !== undefined;
  const feedbackIsCorrect = item.feedback?.isCorrect ?? false;

  // Determine the display value (corrected or extracted)
  const displayValue = hasCorrectedValue ? item.userCorrectedValue : item.extractedValue;

  return (
    <tr
      className={`${styles.row} ${hasCorrectedValue ? styles.correctedRow : ''}`}
      data-item-id={item.id}
    >
      {/* Field Name Cell */}
      <td className={styles.cell}>
        <span className={styles.fieldName}>{item.field}</span>
      </td>

      {/* Value Cell */}
      <td className={styles.cell}>
        <div className={styles.valueContainer}>
          <span className={`${styles.value} ${hasCorrectedValue ? styles.correctedValue : ''}`}>
            {displayValue}
          </span>
          {hasCorrectedValue && (
            <div className={styles.originalValue}>
              <span className={styles.originalLabel}>Original:</span>
              <span className={styles.originalText}>{item.extractedValue}</span>
            </div>
          )}
        </div>
      </td>

      {/* Confidence Cell */}
      <td className={styles.cell}>
        <ConfidenceBadge level={item.confidence} showLabel />
      </td>

      {/* Citations Cell */}
      <td className={styles.cell}>
        <div className={styles.citationsContainer}>
          {item.citations.length > 0 ? (
            item.citations.map((citation, index) => (
              <CitationLink
                key={citation.id}
                citation={citation}
                onClick={onCitationClick}
                index={index + 1}
                compact
              />
            ))
          ) : (
            <span className={styles.noCitations}>No citations</span>
          )}
        </div>
      </td>

      {/* Feedback Cell */}
      <td className={styles.cell}>
        <div className={styles.feedbackContainer}>
          <FeedbackButton
            itemId={item.id}
            onClick={onFeedbackClick}
            hasFeedback={hasFeedback}
            feedbackIsCorrect={feedbackIsCorrect}
            size="small"
          />
        </div>
      </td>
    </tr>
  );
};

export default EvidenceTable;
